import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useHardDeleteBewerber } from '../../api/bewerber';
import type { HardDeleteBewerberDto, Bewerber } from '../../types/bewerber';

interface HardDeleteModalProps {
  isOpen: boolean;
  onClose: () => void;
  bewerber: Bewerber;
  onSuccess?: () => void;
}

export function HardDeleteModal({
  isOpen,
  onClose,
  bewerber,
  onSuccess,
}: HardDeleteModalProps) {
  const hardDeleteMutation = useHardDeleteBewerber();
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
  } = useForm<HardDeleteBewerberDto>({
    defaultValues: {
      reason: '',
    },
  });

  const handleFormSubmit = async (data: HardDeleteBewerberDto) => {
    setError(null);
    try {
      await hardDeleteMutation.mutateAsync({
        id: bewerber.id,
        data,
      });
      toast.success('Bewerber wurde permanent gelöscht');
      reset();
      onClose();
      onSuccess?.();
    } catch (err) {
      setError('Fehler beim Löschen des Bewerbers');
      toast.error('Fehler beim Löschen des Bewerbers');
      console.error('Failed to hard delete bewerber:', err);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  const bewerberName = `${bewerber.firstName} ${bewerber.lastName}`;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Bewerber permanent löschen
                  </h3>

                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="flex">
                      <svg className="h-5 w-5 text-red-400 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-red-800">
                          Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
                        </p>
                        <p className="text-sm text-red-700 mt-1">
                          Alle Daten von <span className="font-medium">{bewerberName}</span> werden unwiderruflich gelöscht, einschließlich Dokumente, Aktivitäten und Verlauf.
                        </p>
                      </div>
                    </div>
                  </div>

                  {error && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}

                  <div className="mt-4">
                    <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                      Löschgrund (optional)
                    </label>
                    <textarea
                      id="reason"
                      rows={3}
                      {...register('reason')}
                      placeholder="z.B. DSGVO-Löschantrag, Fehlerhafte Anlage, etc."
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Optional: Wird für Compliance-Zwecke im Audit-Log gespeichert.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="submit"
                disabled={hardDeleteMutation.isPending}
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
              >
                {hardDeleteMutation.isPending ? 'Wird gelöscht...' : 'Permanent löschen'}
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}
